import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import '../styles/GroupDetail.css';
import Chat from '../components/Chat';

const API_URL = process.env.REACT_APP_API_URL;

const CATEGORY_TONE = { Ders: 'ders', Proje: 'proje', 'Sınav': 'sinav', Genel: 'genel' };
const MAX_VISIBLE_MEMBERS = 8;

// Groups.js ile aynı katılım türleri
const JOIN_TYPES = [
  { value: 'approval', label: 'Başvuru ile (yönetici onayı)', hint: 'Katılmak isteyenler başvuru gönderir, siz onaylarsınız.' },
  { value: 'code', label: 'Davet kodu ile', hint: 'Yalnızca davet kodunu bilenler katılabilir.' },
  { value: 'open', label: 'Herkese açık', hint: 'Herkes tek tıkla katılabilir.' },
];
const JOIN_LABELS = { open: 'Herkese açık', approval: 'Onaylı', code: 'Kodlu' };

// Backend farklı alan adlarıyla dönebileceği için isim tek yerden güvenli biçimde üretilir
const getDisplayName = (person) => {
  if (!person) return '';
  if (typeof person === 'string') return person;
  if (typeof person !== 'object') return '';
  const first = person.first_name || person.firstName || '';
  const last = person.last_name || person.lastName || '';
  const full = `${first} ${last}`.trim();
  return (
    full ||
    person.fullName ||
    person.full_name ||
    person.name ||
    person.username ||
    (person.email ? person.email.split('@')[0] : '')
  );
};

const getInitials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase('tr'))
    .join('') || '?';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem('token');
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId ?? payload.id ?? null;
  } catch (e) {
    return null;
  }
};

// Kimlik doğrulamalı istek: hata durumunda sunucunun mesajını Error olarak fırlatır
const authFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) {
    const error = new Error('Oturum bulunamadı. Lütfen giriş yapınız.');
    error.status = 401;
    throw error;
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }

  if (!res.ok) {
    const error = new Error((data && (data.message || data.error)) || `Hata: ${res.status}`);
    error.status = res.status;
    throw error;
  }
  return data;
};

const ICONS = {
  back: (<><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></>),
  info: (<><circle cx="12" cy="12" r="10" /><path d="M12 16v-4m0-4h.01" /></>),
  chat: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  alert: (<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>),
  search: (<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  users: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />,
  copy: (<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>),
  logout: (<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></>),
  trash: (<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>),
  calendar: (<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>),
  school: (<><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c3 2 9 2 12 0v-5" /></>),
};

const Icon = ({ name, className = '' }) => (
  <svg className={`gd-icon ${className}`} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const [group, setGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [activeTab, setActiveTab] = useState('chat'); // dar ekranlarda: 'chat' | 'info'
  const [manage, setManage] = useState(null); // yalnızca yönetici için
  const [busy, setBusy] = useState(false);
  const manageRef = useRef(null);

  const currentUserId = useMemo(getCurrentUserId, []);

  // Grup bilgisi + üyeler. silent=true: ekranı "Yükleniyor"a çevirmeden arka planda yeniler
  const loadGroup = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const groupData = await authFetch(`/api/groups/${groupId}`);
        setGroup(groupData);

        try {
          const membersData = await authFetch(`/api/groups/${groupId}/members`);
          if (Array.isArray(membersData)) setGroupMembers(membersData);
        } catch (memberErr) {
          console.error('Grup üyeleri alınamadı:', memberErr);
        }
      } catch (err) {
        console.error('GroupDetail fetch error:', err);
        if (err.status === 401) {
          showNotification(err.message, 'error');
          navigate('/login');
          return;
        }
        // Sessiz yenilemede geçici ağ hatalarını yok say; erişim kaybı/silinme durumunda çık
        if (silent && err.status !== 403 && err.status !== 404) return;
        setError(err.message);
        showNotification(err.message, 'error');
        navigate('/groups');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [groupId, navigate, showNotification]
  );

  useEffect(() => {
    loadGroup();
  }, [loadGroup]);

  // Yönetici verileri: katılım türü, davet kodu, bekleyen başvurular
  const adminId = group ? (group.admin_id ?? group.adminId ?? group.created_by ?? group.creator_id) : null;
  const isAdmin = currentUserId != null && adminId != null && String(adminId) === String(currentUserId);

  const loadManage = useCallback(async () => {
    try {
      setManage(await authFetch(`/api/groups/${groupId}/manage`));
    } catch (err) {
      showNotification(err.message, 'error');
    }
  }, [groupId, showNotification]);

  useEffect(() => {
    if (isAdmin) loadManage();
    else setManage(null);
  }, [isAdmin, loadManage]);

  // Sekmeye geri dönünce güncel bilgiyi (yeni üye, yeni başvuru) sessizce çek
  useEffect(() => {
    const onFocus = () => {
      loadGroup({ silent: true });
      if (isAdmin) loadManage();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [loadGroup, loadManage, isAdmin]);

  // Yönetici işlemlerini ortak çalıştırır
  const runAction = async (action, successMessage) => {
    setBusy(true);
    try {
      await action();
      if (successMessage) showNotification(successMessage, 'success');
      await Promise.all([loadManage(), loadGroup({ silent: true })]);
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const changeJoinType = (joinType) =>
    runAction(
      () => authFetch(`/api/groups/${groupId}/settings`, { method: 'PUT', body: JSON.stringify({ joinType }) }),
      'Katılım türü güncellendi.'
    );

  const regenerateCode = () =>
    runAction(
      () => authFetch(`/api/groups/${groupId}/settings`, { method: 'PUT', body: JSON.stringify({ regenerateCode: true }) }),
      'Yeni davet kodu oluşturuldu. Eski kod artık çalışmaz.'
    );

  const decideRequest = (requestId, action) =>
    runAction(
      () => authFetch(`/api/groups/${groupId}/requests/${requestId}`, { method: 'PUT', body: JSON.stringify({ action }) }),
      action === 'approve' ? 'Başvuru onaylandı.' : 'Başvuru reddedildi.'
    );

  const removeMember = (member) =>
    showNotification(
      `${member.name} gruptan çıkarılsın mı?`,
      'confirm',
      () =>
        runAction(
          () => authFetch(`/api/groups/${groupId}/members/${member.id}`, { method: 'DELETE' }),
          'Üye gruptan çıkarıldı.'
        ),
      () => {}
    );

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(manage.invite_code);
      showNotification('Davet kodu kopyalandı.', 'success');
    } catch (e) {
      showNotification('Kod kopyalanamadı.', 'error');
    }
  };

  const leaveGroup = () =>
    showNotification(
      'Gruptan ayrılmak istediğine emin misin?',
      'confirm',
      async () => {
        try {
          await authFetch(`/api/groups/${groupId}/leave`, { method: 'POST' });
          showNotification('Gruptan başarıyla ayrıldınız!', 'success');
          navigate('/groups');
        } catch (err) {
          showNotification('Gruptan ayrılırken hata: ' + err.message, 'error');
        }
      },
      () => {}
    );

  const deleteGroup = () =>
    showNotification(
      'Grubu kapatmak istediğinden emin misin? Bu işlem geri alınamaz.',
      'confirm',
      async () => {
        try {
          await authFetch(`/api/groups/${groupId}`, { method: 'DELETE' });
          showNotification('Grup başarıyla kapandı!', 'success');
          navigate('/groups');
        } catch (err) {
          showNotification('Grup kapatırken hata: ' + err.message, 'error');
        }
      },
      () => {}
    );

  const goToManage = () => {
    setActiveTab('info');
    // Dar ekranda sekme değişimi render edildikten sonra kaydır
    requestAnimationFrame(() => {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      manageRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  };

  /* ---------- Durum ekranları ---------- */

  if (loading) {
    return (
      <div className="gd-page gd-state-page">
        <div className="gd-spinner" role="status" aria-label="Yükleniyor"></div>
        <p>Grup yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gd-page gd-state-page">
        <span className="gd-state-icon is-error"><Icon name="alert" /></span>
        <p>{error}</p>
        <button type="button" onClick={() => navigate('/groups')} className="gd-btn gd-btn-solid">
          Gruplara geri dön
        </button>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="gd-page gd-state-page">
        <span className="gd-state-icon"><Icon name="search" /></span>
        <p>Grup bulunamadı.</p>
        <button type="button" onClick={() => navigate('/groups')} className="gd-btn gd-btn-solid">
          Tüm gruplara dön
        </button>
      </div>
    );
  }

  /* ---------- Türetilmiş veriler ---------- */

  // Üye isimleri: önce /members yanıtı, yoksa grup yanıtındaki nesne biçimli üyeler
  const sourceMembers = groupMembers.length > 0
    ? groupMembers
    : (Array.isArray(group.members) ? group.members.filter((m) => m && typeof m === 'object') : []);
  const memberCount = groupMembers.length || (Array.isArray(group.members) ? group.members.length : 0);

  const isAdminOf = (m) => adminId != null && String(m.id) === String(adminId);

  const adminMember = sourceMembers.find(isAdminOf);
  const creatorName = getDisplayName(adminMember) || group.admin_username || group.adminName || 'Belirtilmemiş';

  const memberItems = sourceMembers
    .map((m, i) => ({
      key: m.id ?? i,
      id: m.id,
      name: getDisplayName(m) || 'İsimsiz kullanıcı',
      isAdmin: isAdminOf(m),
      isMe: currentUserId != null && String(m.id) === String(currentUserId),
    }))
    .sort((a, b) =>
      Number(b.isAdmin) - Number(a.isAdmin) ||
      Number(b.isMe) - Number(a.isMe) ||
      a.name.localeCompare(b.name, 'tr')
    );

  const query = memberQuery.trim().toLocaleLowerCase('tr');
  const filteredMembers = query
    ? memberItems.filter((m) => m.name.toLocaleLowerCase('tr').includes(query))
    : memberItems;
  const visibleMembers = showAllMembers || query ? filteredMembers : filteredMembers.slice(0, MAX_VISIBLE_MEMBERS);
  const hiddenCount = filteredMembers.length - MAX_VISIBLE_MEMBERS;

  const joinType = group.join_type || 'open';
  const createdAt = formatDate(group.created_at);
  const pendingCount = manage?.requests?.length ?? group.pending_requests ?? 0;

  return (
    <div className="gd-page">
      {/* Başlık */}
      <header className="gd-head">
        <div className="gd-container">
          <button type="button" onClick={() => navigate('/groups')} className="gd-back">
            <Icon name="back" /> Gruplara dön
          </button>

          <div className="gd-head-row">
            <div className="gd-head-copy">
              <div className="gd-title-row">
                <h1>{group.name}</h1>
                <div className="gd-badges">
                  {group.category && (
                    <span className="gd-tag" data-tone={CATEGORY_TONE[group.category] || 'genel'}>{group.category}</span>
                  )}
                  <span className="gd-join" data-type={joinType}>{JOIN_LABELS[joinType] || JOIN_LABELS.open}</span>
                  {isAdmin && <span className="gd-role">Yönetici</span>}
                </div>
              </div>

              <ul className="gd-stats" aria-label="Grup özeti">
                <li><Icon name="users" /> {memberCount} üye</li>
                {group.school && group.school.trim() !== '' && <li><Icon name="school" /> {group.school}</li>}
                {createdAt && <li><Icon name="calendar" /> {createdAt}</li>}
              </ul>
            </div>

            <div className="gd-head-actions">
              {isAdmin ? (
                <>
                  <button type="button" className="gd-btn gd-btn-outline" onClick={goToManage}>
                    <Icon name="shield" /> Yönetim
                    {pendingCount > 0 && <span className="gd-badge" aria-label={`${pendingCount} bekleyen başvuru`}>{pendingCount}</span>}
                  </button>
                  <button type="button" className="gd-btn gd-btn-danger-outline" onClick={deleteGroup}>
                    <Icon name="trash" /> Grubu Kapat
                  </button>
                </>
              ) : (
                <button type="button" className="gd-btn gd-btn-danger-outline" onClick={leaveGroup}>
                  <Icon name="logout" /> Gruptan Ayrıl
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Dar ekranlarda sohbet / grup bilgisi sekmeleri */}
      <div className="gd-container">
        <div className="gd-tabs" role="tablist" aria-label="Bölümler">
          <button
            type="button"
            role="tab"
            id="gd-tab-chat"
            aria-selected={activeTab === 'chat'}
            aria-controls="gd-chat-panel"
            className={`gd-tab${activeTab === 'chat' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            <Icon name="chat" /> Sohbet
          </button>
          <button
            type="button"
            role="tab"
            id="gd-tab-info"
            aria-selected={activeTab === 'info'}
            aria-controls="gd-side-panel"
            className={`gd-tab${activeTab === 'info' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <Icon name="info" /> Grup
            {isAdmin && pendingCount > 0 && <span className="gd-badge">{pendingCount}</span>}
          </button>
        </div>
      </div>

      <main className="gd-container gd-layout" data-tab={activeTab}>
        <div className="gd-side" id="gd-side-panel" role="tabpanel" aria-labelledby="gd-tab-info">
          {/* Grup bilgileri */}
          <section className="gd-card gd-info" aria-labelledby="gd-info-title">
            <h2 id="gd-info-title" className="gd-card-title">
              <span className="gd-card-icon"><Icon name="info" /></span>
              Grup hakkında
            </h2>

            <dl className="gd-list">
              <div className="gd-item">
                <dt>Açıklama</dt>
                <dd>{group.description || 'Açıklama eklenmemiş.'}</dd>
              </div>
              <div className="gd-item">
                <dt>Oluşturan</dt>
                <dd>{creatorName}</dd>
              </div>
              {group.school && group.school.trim() !== '' && (
                <div className="gd-item">
                  <dt>Okul</dt>
                  <dd>{group.school}</dd>
                </div>
              )}
              <div className="gd-item">
                <dt>Katılım</dt>
                <dd>{JOIN_TYPES.find((t) => t.value === joinType)?.label || JOIN_LABELS.open}</dd>
              </div>
              {createdAt && (
                <div className="gd-item">
                  <dt>Oluşturulma</dt>
                  <dd>{createdAt}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Üyeler */}
          <section className="gd-card gd-members-card" aria-labelledby="gd-members-title">
            <h2 id="gd-members-title" className="gd-card-title">
              <span className="gd-card-icon"><Icon name="users" /></span>
              Üyeler
              <span className="gd-pill">{memberCount}</span>
            </h2>

            {memberItems.length > MAX_VISIBLE_MEMBERS && (
              <label className="gd-search">
                <Icon name="search" className="gd-search-icon" />
                <input
                  type="search"
                  className="gd-search-input"
                  placeholder="Üye ara..."
                  aria-label="Üye ara"
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                />
              </label>
            )}

            {memberItems.length > 0 ? (
              <>
                {visibleMembers.length > 0 ? (
                  <ul className="gd-members">
                    {visibleMembers.map((member) => (
                      <li
                        key={member.key}
                        className={`gd-member${member.isAdmin ? ' is-admin' : ''}${member.isMe ? ' is-me' : ''}`}
                      >
                        <span className="gd-avatar" aria-hidden="true">{getInitials(member.name)}</span>
                        <span className="gd-member-name" title={member.name}>{member.name}</span>
                        {member.isAdmin && <span className="gd-role">Yönetici</span>}
                        {member.isMe && !member.isAdmin && <span className="gd-role is-me">Siz</span>}
                        {isAdmin && !member.isAdmin && member.id != null && (
                          <button
                            type="button"
                            className="gd-btn gd-btn-danger-outline gd-btn-sm"
                            disabled={busy}
                            onClick={() => removeMember(member)}
                          >
                            Çıkar
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="gd-muted">Aramayla eşleşen üye yok.</p>
                )}

                {!query && hiddenCount > 0 && (
                  <button type="button" className="gd-link-btn" onClick={() => setShowAllMembers((v) => !v)}>
                    {showAllMembers ? 'Daha az göster' : `Tümünü göster (+${hiddenCount})`}
                  </button>
                )}
              </>
            ) : (
              <p className="gd-muted">
                {memberCount > 0 ? 'Üye isimleri yüklenemedi.' : 'Henüz üye yok.'}
              </p>
            )}
          </section>

          {/* Yönetim paneli (yalnızca yönetici) */}
          {isAdmin && (
            <section className="gd-card gd-manage" ref={manageRef} aria-labelledby="gd-manage-title">
              <h2 id="gd-manage-title" className="gd-card-title">
                <span className="gd-card-icon"><Icon name="shield" /></span>
                Yönetim
              </h2>

              {!manage ? (
                <p className="gd-muted">Yönetim bilgileri yükleniyor...</p>
              ) : (
                <div className="gd-manage-body">
                  <div className="gd-section">
                    <h3>Katılım türü</h3>
                    <div className="gd-radio-list">
                      {JOIN_TYPES.map((t) => (
                        <label key={t.value} className={`gd-radio${manage.join_type === t.value ? ' is-active' : ''}`}>
                          <input
                            type="radio"
                            name="join-type"
                            value={t.value}
                            checked={manage.join_type === t.value}
                            disabled={busy}
                            onChange={() => changeJoinType(t.value)}
                          />
                          <span><b>{t.label}</b><small>{t.hint}</small></span>
                        </label>
                      ))}
                    </div>

                    {manage.join_type === 'code' && (
                      <div className="gd-code-box">
                        <span className="gd-code-label">Davet kodu</span>
                        <div className="gd-code-row">
                          <code className="gd-code">{manage.invite_code}</code>
                          <button type="button" className="gd-btn gd-btn-outline gd-btn-sm" onClick={copyCode}>
                            <Icon name="copy" /> Kopyala
                          </button>
                          <button type="button" className="gd-btn gd-btn-outline gd-btn-sm" onClick={regenerateCode} disabled={busy}>
                            Yenile
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="gd-section">
                    <h3>Bekleyen başvurular <span className="gd-pill">{manage.requests.length}</span></h3>
                    {manage.requests.length === 0 ? (
                      <p className="gd-muted">Bekleyen başvuru yok.</p>
                    ) : (
                      <ul className="gd-requests">
                        {manage.requests.map((r) => {
                          const name = getDisplayName(r) || 'İsimsiz kullanıcı';
                          return (
                            <li key={r.id}>
                              <span className="gd-avatar" aria-hidden="true">{getInitials(name)}</span>
                              <span className="gd-member-name" title={name}>{name}</span>
                              <span className="gd-request-actions">
                                <button type="button" className="gd-btn gd-btn-solid gd-btn-sm" disabled={busy} onClick={() => decideRequest(r.id, 'approve')}>Onayla</button>
                                <button type="button" className="gd-btn gd-btn-danger-outline gd-btn-sm" disabled={busy} onClick={() => decideRequest(r.id, 'reject')}>Reddet</button>
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Grup sohbeti */}
        <section
          className="gd-card gd-chat"
          id="gd-chat-panel"
          role="tabpanel"
          aria-labelledby="gd-chat-title"
        >
          <h2 id="gd-chat-title" className="gd-card-title">
            <span className="gd-card-icon"><Icon name="chat" /></span>
            Grup sohbeti
          </h2>
          <div className="gd-chat-box">
            <Chat groupId={groupId} />
          </div>
        </section>
      </main>
    </div>
  );
}

export default GroupDetail;